import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { fetchData } from "../../api/client";
import FlowCanvas from "./flowBuilder/FlowCanvas";
import { migrateTriggersToGraph } from "./flowBuilder/graphModel";
import CampaignMetricsHeader from "./components/CampaignMetricsHeader";
import CampaignFunnelView from "./components/CampaignFunnelView";
import StageConfigPanel from "./components/StageConfigPanel";

function safeJsonParse(s) {
  try {
    return s ? JSON.parse(s) : null;
  } catch {
    return null;
  }
}

function prettyJson(obj) {
  try {
    return JSON.stringify(obj, null, 2);
  } catch {
    return "{}";
  }
}

function toFriendlyMessage(err) {
  const raw =
    (err && typeof err === "object" && (err.message || err.errorMessage || err.error)) ||
    (typeof err === "string" ? err : "") ||
    "";
  const status = err?.status ?? err?.response?.status ?? err?.response?.data?.status ?? null;
  if (status >= 500) return "Erro temporário no servidor. Tente novamente em instantes.";
  if (status === 401 || status === 403) return "Você não tem permissão para acessar este recurso.";
  if (status === 404) return "Recurso não encontrado.";
  return raw || "Não foi possível concluir a operação. Tente novamente.";
}

export default function CampaignDetailPage() {
  const navigate = useNavigate();
  const params = useParams();
  const campaignId = params?.id ? Number(params.id) : null;

  const [campaign, setCampaign] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [flows, setFlows] = useState([]);

  const [advancedMode, setAdvancedMode] = useState(false);
  const [editorMode, setEditorMode] = useState("visual"); // visual | json
  const [flowConfigDraft, setFlowConfigDraft] = useState("");

  const [stagePanelOpen, setStagePanelOpen] = useState(false);
  const [activeStageId, setActiveStageId] = useState(null);

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const selectedFlowId = campaign?.flowId ?? null;
  const selectedFlow = useMemo(() => flows.find((f) => f.id === selectedFlowId) || null, [flows, selectedFlowId]);

  const refreshCampaign = async () => {
    if (!campaignId) return;
    setLoading(true);
    setErrorMsg("");
    try {
      const res = await fetchData(`/campaigns/${campaignId}`, "GET");
      setCampaign(res?.data || null);
    } catch (err) {
      console.error("Erro ao carregar campanha:", err);
      setErrorMsg(toFriendlyMessage(err));
      setCampaign(null);
    } finally {
      setLoading(false);
    }
  };

  const refreshFlows = async () => {
    try {
      const flowRes = await fetchData("/flows", "GET");
      setFlows(flowRes?.data || []);
    } catch (err) {
      console.error("Erro ao carregar flows:", err);
    }
  };

  const refreshMetrics = async () => {
    if (!campaignId) return;
    try {
      const res = await fetchData(`/campaigns/${campaignId}/metrics`, "GET");
      setMetrics(res?.data || null);
    } catch (err) {
      console.error("Erro ao carregar métricas:", err);
      setMetrics(null);
    }
  };

  useEffect(() => {
    refreshCampaign();
    refreshFlows();
    refreshMetrics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  useEffect(() => {
    if (!selectedFlow) return;
    setFlowConfigDraft(prettyJson(selectedFlow.config || {}));
  }, [selectedFlow]);

  const selectedFlowConfigObj = useMemo(() => safeJsonParse(flowConfigDraft) || null, [flowConfigDraft]);
  const hasGraph = !!(selectedFlowConfigObj && selectedFlowConfigObj.graph);
  const hasMvpTriggerActions = useMemo(() => {
    const cfg = selectedFlowConfigObj && typeof selectedFlowConfigObj === "object" ? selectedFlowConfigObj : {};
    const t0 = Array.isArray(cfg.triggers) ? cfg.triggers[0] || {} : {};
    return !!(t0?.createLead || t0?.tag || t0?.dmText);
  }, [selectedFlowConfigObj]);

  const saveFlowConfig = async () => {
    const parsed = safeJsonParse(flowConfigDraft);
    if (!parsed) {
      alert("Config JSON inválida.");
      return;
    }
    if (!selectedFlow) return;
    setErrorMsg("");
    try {
      await fetchData(`/flows/${selectedFlow.id}`, "PUT", { config: parsed });
      await refreshFlows();
      await refreshMetrics();
    } catch (err) {
      console.error("Erro ao salvar config:", err);
      setErrorMsg(toFriendlyMessage(err));
    }
  };

  const updateUxFunnelStage = (stageId, patch) => {
    const cfg = safeJsonParse(flowConfigDraft) || {};
    const ux = cfg.uxFunnel && typeof cfg.uxFunnel === "object" ? cfg.uxFunnel : {};
    const stages = ux.stages && typeof ux.stages === "object" ? ux.stages : {};
    const prev = stages[stageId] && typeof stages[stageId] === "object" ? stages[stageId] : {};
    const next = {
      ...cfg,
      uxFunnel: {
        ...ux,
        stages: {
          ...stages,
          [stageId]: { ...prev, ...patch },
        },
      },
    };
    setFlowConfigDraft(prettyJson(next));
  };

  return (
    <Box p={4}>
      {errorMsg ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMsg}
        </Alert>
      ) : null}

      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} mb={2}>
        <Box>
          <Typography variant="h4" noWrap title={campaign?.name || ""}>
            {campaign?.name || "Campanha"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Visualize e configure seu funil por etapas. Use o modo avançado para editar o flow completo.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button variant="outlined" onClick={() => navigate("/campaigns")}>
            Voltar
          </Button>
          <Button variant="outlined" onClick={() => { refreshCampaign(); refreshFlows(); refreshMetrics(); }} disabled={loading}>
            Atualizar
          </Button>
          <FormControlLabel
            control={<Switch checked={advancedMode} onChange={(e) => setAdvancedMode(e.target.checked)} />}
            label="Modo avançado"
          />
        </Stack>
      </Stack>

      <CampaignMetricsHeader campaign={campaign} metrics={metrics} />

      {!campaign ? (
        <Typography color="text.secondary">Carregando…</Typography>
      ) : (
        <>
          {!advancedMode ? (
            <CampaignFunnelView
              campaign={campaign}
              metrics={metrics}
              flowConfig={selectedFlowConfigObj}
              onOpenStage={(stageId) => {
                setActiveStageId(stageId);
                setStagePanelOpen(true);
              }}
            />
          ) : (
            <Card variant="outlined">
              <CardContent>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.5}
                  alignItems={{ sm: "center" }}
                  justifyContent="space-between"
                  mb={1}
                >
                  <Box>
                    <Typography fontWeight={900}>Flow (avançado)</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Flow id: {campaign.flowId || "—"} • Status: {campaign.status}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="flex-end">
                    <ToggleButtonGroup
                      size="small"
                      value={editorMode}
                      exclusive
                      onChange={(_, v) => (v ? setEditorMode(v) : null)}
                    >
                      <ToggleButton value="visual">Visual</ToggleButton>
                      <ToggleButton value="json">JSON</ToggleButton>
                    </ToggleButtonGroup>
                    {editorMode === "visual" && hasMvpTriggerActions ? (
                      <Button
                        size="small"
                        variant="outlined"
                        color="warning"
                        onClick={() => {
                          const parsed = selectedFlowConfigObj;
                          if (!parsed) return alert("Config JSON inválida. Corrija antes de migrar.");
                          const next = migrateTriggersToGraph(parsed);
                          setFlowConfigDraft(prettyJson(next));
                        }}
                      >
                        Migrar MVP → Grafo
                      </Button>
                    ) : null}
                    <Button size="small" variant="contained" onClick={saveFlowConfig} disabled={!selectedFlow}>
                      Salvar
                    </Button>
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => setFlowConfigDraft(prettyJson(selectedFlow?.config || {}))}
                      disabled={!selectedFlow}
                    >
                      Desfazer
                    </Button>
                  </Stack>
                </Stack>

                {!selectedFlow ? (
                  <Alert severity="info">Nenhum flow associado ainda (ou não carregado).</Alert>
                ) : editorMode === "visual" ? (
                  <>
                    {!selectedFlowConfigObj ? (
                      <Alert severity="warning" sx={{ mb: 1 }}>
                        O JSON atual está inválido. Troque para o modo JSON e corrija para usar o builder visual.
                      </Alert>
                    ) : null}
                    {hasGraph && hasMvpTriggerActions ? (
                      <Alert severity="warning" sx={{ mb: 1 }}>
                        Atenção: este flow tem <strong>graph</strong> e também ações no <strong>trigger</strong>. Isso pode duplicar ações.
                      </Alert>
                    ) : null}
                    {selectedFlowConfigObj ? (
                      <FlowCanvas config={selectedFlowConfigObj} onChangeConfig={(nextCfg) => setFlowConfigDraft(prettyJson(nextCfg))} />
                    ) : null}
                  </>
                ) : (
                  <Box>
                    <textarea
                      value={flowConfigDraft}
                      onChange={(e) => setFlowConfigDraft(e.target.value)}
                      style={{
                        width: "100%",
                        minHeight: 320,
                        fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
                        fontSize: 12,
                        padding: 12,
                        borderRadius: 8,
                        border: "1px solid rgba(0,0,0,0.12)",
                      }}
                      spellCheck={false}
                    />
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  Dica: no modo simples, as configurações por etapa são salvas em <code>config.uxFunnel</code> e não afetam a execução do engine.
                </Typography>
              </CardContent>
            </Card>
          )}
        </>
      )}

      <StageConfigPanel
        open={stagePanelOpen}
        stageId={activeStageId}
        flowConfig={selectedFlowConfigObj}
        onClose={() => setStagePanelOpen(false)}
        onChange={(patch) => {
          if (!activeStageId) return;
          updateUxFunnelStage(activeStageId, patch);
        }}
        onSave={saveFlowConfig}
      />
    </Box>
  );
}

